/**
 * The AI assistant — a global, floating chat panel (not a ModuleLoader module).
 *
 * Loaded from index.html as a self-initialising bundle (portal.html discards an
 * external script-src on re-inject). It waits for window._fosUser plus the
 * portal header, asks /assistant/status and injects the 🤖 button next to the
 * ❓ help button only where the assistant is enabled.
 *
 * The security and UI rules:
 *  - Every backend call goes through window.Api (auth + impersonation + scope).
 *  - Model text is escaped first, then formatted markdown-lite — never raw HTML.
 *  - Deeplinks arrive as server-validated action events (an allowlist in the
 *    backend) and open modules through a tab click or window.fOSOpenResource.
 *  - The panel hangs DIRECTLY off document.body (a backdrop-filter ancestor
 *    otherwise makes position:fixed a containing-block casualty).
 *  - The history lives in sessionStorage alone (no server persistence, easy on the GDPR).
 */
import { escapeHtml } from "~shared/escape";

type ChatMsg = { role: "user" | "assistant"; content: string };
type StreamEvent = {
  type: "token" | "tool" | "action" | "done" | "error";
  text?: string;
  name?: string;
  status?: string;
  message?: string;
  tab?: string;
  resource_type?: string;
  id?: string | number;
};

const HISTORY_KEY = "fos_assistant_history";
const MAX_HISTORY = 30;

const QUICK_ACTIONS = [
  "Was steht für mich an?",
  "Wie lege ich ein Risiko an?",
  "Wie melde ich einen Vorfall?",
  "Wo finde ich unsere Richtlinien?",
];

const TOOL_LABELS: Record<string, string> = {
  get_my_open_items: "Prüfe deine offenen Punkte …",
  list_risks: "Durchsuche die Risiken …",
  list_overdue_measures: "Prüfe fällige Maßnahmen …",
  get_kpi_overview: "Hole die Kennzahlen …",
  search_knowledge: "Durchsuche die Wissensbasis …",
  get_module_catalog: "Sehe die Module durch …",
};

let panel: HTMLElement | null = null;
let busy = false;
let history: ChatMsg[] = [];

function api(): any { return (window as any).Api; }

function loadHistory(): void {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    history = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(history)) history = [];
  } catch { history = []; }
}

function saveHistory(): void {
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(-MAX_HISTORY)));
  } catch { /* quota — the history is dispensable */ }
}

/** Markdown-lite: escape first, then **bold**, lists and paragraphs. */
function renderMarkdownLite(src: string): string {
  const esc = escapeHtml(src);
  const withBold = esc.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  const lines = withBold.split(/\r?\n/);
  let html = "";
  let inList = false;
  for (const line of lines) {
    const li = line.match(/^\s*(?:[-•]|\d+[.)])\s+(.*)$/);
    if (li) {
      if (!inList) { html += "<ul class='list-disc pl-5 my-1 space-y-0.5'>"; inList = true; }
      html += `<li>${li[1]}</li>`;
      continue;
    }
    if (inList) { html += "</ul>"; inList = false; }
    if (line.trim() === "") { html += "<div class='h-1.5'></div>"; continue; }
    html += `<div>${line}</div>`;
  }
  if (inList) html += "</ul>";
  return html;
}

function openDeeplink(ev: StreamEvent): void {
  const w = window as any;
  minimizePanel();
  if (ev.resource_type && ev.id != null && typeof w.fOSOpenResource === "function") {
    w.fOSOpenResource(ev.resource_type, ev.id, ev.tab);
    return;
  }
  if (ev.tab && typeof w.fOSOpenResource === "function") {
    // Modul-only-Deeplink: fOSOpenResource mit unbekanntem Typ + moduleFallback
    w.fOSOpenResource("__module__", null, ev.tab);
    return;
  }
  if (ev.tab) {
    (document.querySelector(`.da-tab[data-tab="${ev.tab}"]`) as HTMLElement | null)?.click();
  }
}

function tabLabel(tab: string): string {
  const el = document.querySelector(`.da-tab[data-tab="${tab}"]`);
  return (el?.textContent || tab).trim();
}

// ---------------------------------------------------------------------------
// Panel-DOM
// ---------------------------------------------------------------------------
function ensurePanel(): HTMLElement {
  if (panel) return panel;
  panel = document.createElement("div");
  panel.id = "assistant-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "KI-Assistent");
  panel.style.cssText = [
    "position:fixed", "right:16px", "bottom:16px", "z-index:9000",
    "width:min(400px, calc(100vw - 24px))", "height:min(620px, calc(100vh - 32px))",
    "display:none", "flex-direction:column", "background:#fff",
    "border:1px solid #e2e8f0", "border-radius:14px",
    "box-shadow:0 12px 40px rgba(0,0,0,.18)", "overflow:hidden",
  ].join(";");
  panel.innerHTML = `
    <div class="da-grad" style="display:flex;align-items:center;gap:8px;padding:10px 14px;color:#fff">
      <span style="font-size:18px">🤖</span>
      <div style="flex:1;min-width:0">
        <div style="font-weight:700;font-size:14px;line-height:1.1">fOS-Assistent</div>
        <div style="font-size:10px;opacity:.85">KI-generiert — Angaben ohne Gewähr</div>
      </div>
      <button id="assistant-reset" title="Neu beginnen" type="button"
              style="background:rgba(255,255,255,.18);border:none;color:#fff;border-radius:8px;padding:4px 8px;font-size:11px;cursor:pointer">Neu</button>
      <button id="assistant-close" title="Schließen" type="button" aria-label="Schließen"
              style="background:rgba(255,255,255,.18);border:none;color:#fff;border-radius:8px;padding:4px 9px;font-size:13px;cursor:pointer">✕</button>
    </div>
    <div id="assistant-messages" style="flex:1;overflow-y:auto;padding:12px;background:#f8fafc;font-size:13px;color:#0f172a"></div>
    <div id="assistant-chips" style="display:flex;flex-wrap:wrap;gap:6px;padding:8px 12px 0;background:#fff"></div>
    <form id="assistant-form" style="display:flex;gap:8px;padding:10px 12px;background:#fff;border-top:1px solid #e2e8f0">
      <input id="assistant-input" type="text" autocomplete="off" maxlength="2000"
             placeholder="Frage zum Portal oder zu deinen Aufgaben …"
             style="flex:1;border:1px solid #cbd5e1;border-radius:10px;padding:8px 12px;font-size:13px;outline-color:var(--fos-green)">
      <button type="submit" id="assistant-send" title="Senden"
              style="background:var(--fos-green);color:#fff;border:none;border-radius:10px;padding:8px 14px;font-weight:700;cursor:pointer">➤</button>
    </form>`;
  document.body.appendChild(panel);

  panel.querySelector("#assistant-close")!.addEventListener("click", minimizePanel);
  panel.querySelector("#assistant-reset")!.addEventListener("click", () => {
    if (busy) return;
    history = [];
    saveHistory();
    renderMessages();
  });
  (panel.querySelector("#assistant-form") as HTMLFormElement).addEventListener("submit", (e) => {
    e.preventDefault();
    const input = panel!.querySelector("#assistant-input") as HTMLInputElement;
    const text = input.value.trim();
    if (text) { input.value = ""; void send(text); }
  });
  renderMessages();
  return panel;
}

function minimizePanel(): void {
  if (panel) panel.style.display = "none";
}

function togglePanel(): void {
  const p = ensurePanel();
  const open = p.style.display !== "none" && p.style.display !== "";
  p.style.display = open ? "none" : "flex";
  if (!open) {
    (p.querySelector("#assistant-input") as HTMLInputElement)?.focus();
    scrollDown();
  }
}

function messagesEl(): HTMLElement {
  return panel!.querySelector("#assistant-messages") as HTMLElement;
}

function scrollDown(): void {
  const el = messagesEl();
  el.scrollTop = el.scrollHeight;
}

function renderChips(): void {
  const chips = panel!.querySelector("#assistant-chips") as HTMLElement;
  if (history.length || busy) { chips.innerHTML = ""; return; }
  chips.innerHTML = QUICK_ACTIONS.map((q) =>
    `<button type="button" class="assistant-chip" data-q="${escapeHtml(q)}"
       style="background:var(--fos-green-l);color:#365314;border:none;border-radius:999px;padding:5px 11px;font-size:12px;cursor:pointer">${escapeHtml(q)}</button>`
  ).join("");
  chips.querySelectorAll(".assistant-chip").forEach((b) => {
    b.addEventListener("click", () => void send((b as HTMLElement).dataset.q || ""));
  });
}

function bubble(role: "user" | "assistant", html: string): string {
  if (role === "user") {
    return `<div style="display:flex;justify-content:flex-end;margin:6px 0">
      <div style="background:var(--fos-green);color:#fff;border-radius:12px 12px 2px 12px;padding:7px 11px;max-width:85%">${html}</div></div>`;
  }
  return `<div style="display:flex;justify-content:flex-start;margin:6px 0">
    <div style="background:#fff;border:1px solid #e2e8f0;border-radius:12px 12px 12px 2px;padding:7px 11px;max-width:90%">${html}</div></div>`;
}

function renderMessages(): void {
  const el = messagesEl();
  if (!history.length) {
    el.innerHTML = `<div style="text-align:center;color:#64748b;padding:24px 12px;font-size:12.5px">
      <div style="font-size:30px;margin-bottom:6px">👋</div>
      Hallo! Ich helfe dir, dich im Portal zurechtzufinden —<br>
      frag mich nach deinen Aufgaben, Modulen oder Abläufen.</div>`;
  } else {
    el.innerHTML = history.map((m) =>
      bubble(m.role, m.role === "user" ? escapeHtml(m.content) : renderMarkdownLite(m.content))
    ).join("");
  }
  renderChips();
  scrollDown();
}

// ---------------------------------------------------------------------------
// Senden + Stream-Verarbeitung
// ---------------------------------------------------------------------------
async function send(text: string): Promise<void> {
  if (busy || !text) return;
  busy = true;
  history.push({ role: "user", content: text });
  saveHistory();
  renderMessages();

  const el = messagesEl();
  const live = document.createElement("div");
  el.appendChild(live);
  const status = document.createElement("div");
  status.style.cssText = "color:#64748b;font-size:11.5px;margin:4px 0 2px";
  status.textContent = "Denke nach …";
  el.appendChild(status);
  scrollDown();

  let answer = "";
  const actions: StreamEvent[] = [];
  let errored: string | null = null;

  const repaint = () => {
    const actionsHtml = actions.length
      ? `<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:6px">` +
        actions.map((a, i) =>
          `<button type="button" class="assistant-action" data-i="${i}"
             style="background:#fff;border:1px solid var(--fos-green);color:#3f6212;border-radius:8px;padding:4px 10px;font-size:12px;cursor:pointer">→ ${escapeHtml(a.resource_type && a.id != null ? String(a.id) : tabLabel(a.tab || ""))} öffnen</button>`
        ).join("") + "</div>"
      : "";
    live.innerHTML = bubble("assistant", renderMarkdownLite(answer || "…") + actionsHtml);
    live.querySelectorAll(".assistant-action").forEach((b) => {
      b.addEventListener("click", () => openDeeplink(actions[Number((b as HTMLElement).dataset.i)]));
    });
    scrollDown();
  };

  try {
    await api().stream("/assistant/chat", { messages: history.slice(-MAX_HISTORY) }, (ev: StreamEvent) => {
      if (ev.type === "token" && ev.text) {
        answer += ev.text;
        repaint();
      } else if (ev.type === "tool") {
        status.textContent = ev.status === "start"
          ? (TOOL_LABELS[ev.name || ""] || "Hole Daten …")
          : "Formuliere die Antwort …";
      } else if (ev.type === "action" && ev.tab) {
        if (!actions.some((a) => a.tab === ev.tab && a.resource_type === ev.resource_type && a.id === ev.id)) {
          actions.push(ev);
        }
        repaint();
      } else if (ev.type === "error") {
        errored = ev.message || "Unbekannter Fehler.";
      }
    });
  } catch (e: any) {
    errored = (e && e.message) || "Der KI-Assistent ist derzeit nicht erreichbar.";
  }

  status.remove();
  busy = false;
  if (errored && !answer) {
    live.innerHTML = bubble("assistant",
      `<span style="color:#b91c1c">${escapeHtml(errored)}</span>`);
    // Do not take the error into the history — the user can simply ask again.
    scrollDown();
    return;
  }
  if (answer) {
    history.push({ role: "assistant", content: answer });
    saveHistory();
  }
  repaint();
}

// ---------------------------------------------------------------------------
// Bootstrap: auf Portal warten, Status pruefen, Button injizieren
// ---------------------------------------------------------------------------
function injectButton(): void {
  if (document.getElementById("assistant-btn")) return;
  const helpBtn = document.getElementById("help-btn");
  if (!helpBtn) return;
  const btn = document.createElement("button");
  btn.id = "assistant-btn";
  btn.type = "button";
  btn.className = helpBtn.className || "app-icon-btn";
  btn.title = "KI-Assistent";
  btn.setAttribute("aria-haspopup", "dialog");
  btn.textContent = "🤖";
  btn.addEventListener("click", togglePanel);
  helpBtn.insertAdjacentElement("beforebegin", btn);
}

async function bootstrapAssistant(): Promise<void> {
  // Wait for the app bootstrap (window._fosUser is set in loadPortal).
  for (let i = 0; i < 120; i++) {
    if ((window as any)._fosUser && document.getElementById("help-btn")) break;
    await new Promise((r) => setTimeout(r, 500));
  }
  if (!(window as any)._fosUser) return;
  let enabled = false;
  try {
    const status = await api().get("/assistant/status");
    enabled = !!(status && status.enabled);
  } catch (e) {
    console.warn("[Assistent] Status-Abfrage fehlgeschlagen:", e);
    return;
  }
  if (!enabled) return;
  loadHistory();
  injectButton();
  // The header can be rebuilt on portal re-renders -> re-inject the button.
  const obs = new MutationObserver(() => injectButton());
  obs.observe(document.body, { childList: true, subtree: true });
}

void bootstrapAssistant();
